import { callUser } from '@/services/firebase/callable'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import { watchJob } from '@/services/aiService'

export default class AiChatService {
  static async runTurn({
    messages,
    tools,
    model,
    provider = 'openrouter',
    files,
    contextSnapshot,
    sessionId,
    turnId,
    latestUserMessage,
    clarificationResolution,
    sessionState,
    personaId,
    agentVersion,
    agentOptions,
    waitForCompletion = true
  } = {}) {
    // Callable bridge; server should accept messages+tools in future step
    const payload = {
      provider,
      model,
      waitForCompletion,
      purpose: 'chat',
      // Keep system prompts so tools and guardrails remain active
      disableSystem: false,
      // Hint the provider to use tools when available
      tool_choice: 'auto'
    }
    if (Array.isArray(messages)) payload.messages = messages
    if (Array.isArray(tools)) payload.tools = tools
    if (Array.isArray(files)) payload.files = files
    if (contextSnapshot && typeof contextSnapshot === 'object') payload.contextSnapshot = contextSnapshot
    if (typeof sessionId === 'string' && sessionId.trim()) payload.sessionId = sessionId.trim()
    if (typeof turnId === 'string' && turnId.trim()) payload.turnId = turnId.trim()
    if (typeof latestUserMessage === 'string' && latestUserMessage.trim()) {
      payload.latestUserMessage = latestUserMessage.trim()
    }
    if (clarificationResolution && typeof clarificationResolution === 'object') {
      payload.clarificationResolution = clarificationResolution
    }
    if (sessionState && typeof sessionState === 'object') payload.sessionState = sessionState
    if (typeof personaId === 'string' && personaId.trim()) payload.personaId = personaId.trim()
    if (agentVersion) payload.agentVersion = agentVersion
    if (agentOptions && typeof agentOptions === 'object') payload.agentOptions = agentOptions
    const { data } = await callUser('aiRun', payload)
    // Surface server-side failures explicitly so callers can show feedback
    if (data?.error) {
      const msg = data.error?.message || data.error?.code || 'AI run failed'
      throw new Error(msg)
    }
    if (data?.status === 'failed') {
      const msg = data?.result?.message || data?.message || 'AI run failed'
      throw new Error(msg)
    }
    // If the callable timed out and left the job queued, poll Firestore until completion
    if (data?.status === 'queued' && data?.jobId) {
      const jobRef = doc(db, 'aiJobs', data.jobId)
      const start = Date.now()
      const timeoutMs = 90000
      while (Date.now() - start < timeoutMs) {
        const snap = await getDoc(jobRef)
        if (snap.exists()) {
          const job = snap.data()
          if (job.status === 'completed')
            return { jobId: data.jobId, status: 'completed', result: job.result }
          if (job.status === 'failed') throw new Error(job.error || 'AI run failed')
          if (job.status === 'cancelled') throw new Error('stream_cancelled')
        }
        await new Promise((r) => setTimeout(r, 1000))
      }
      throw new Error('AI run timed out')
    }
    return data
  }

  static async runTurnStreaming({
    messages,
    tools,
    tool_choice,
    model,
    provider = 'openrouter',
    files,
    onStream,
    agentMode = false,
    agentVersion,
    agentOptions,
    contextSnapshot,
    sessionId,
    turnId,
    latestUserMessage,
    clarificationResolution,
    sessionState,
    personaId,
    timeoutMs = 180000
  } = {}) {
    const normalizedAgentMode = !!agentMode
    const normalizedAgentVersion = normalizedAgentMode ? agentVersion || 'v2' : agentVersion
    const payload = {
      provider,
      model,
      waitForCompletion: false,
      purpose: 'chat',
      // Preserve system prompts to encourage tool use
      disableSystem: false,
      tool_choice: tool_choice || 'auto',
      stream: true,
      agentMode: normalizedAgentMode
    }
    if (Array.isArray(messages)) payload.messages = messages
    if (Array.isArray(tools) && !normalizedAgentMode) payload.tools = tools
    if (Array.isArray(files)) payload.files = files
    if (contextSnapshot && typeof contextSnapshot === 'object') payload.contextSnapshot = contextSnapshot
    if (typeof sessionId === 'string' && sessionId.trim()) payload.sessionId = sessionId.trim()
    if (typeof turnId === 'string' && turnId.trim()) payload.turnId = turnId.trim()
    if (typeof latestUserMessage === 'string' && latestUserMessage.trim()) {
      payload.latestUserMessage = latestUserMessage.trim()
    }
    if (clarificationResolution && typeof clarificationResolution === 'object') {
      payload.clarificationResolution = clarificationResolution
    }
    if (sessionState && typeof sessionState === 'object') payload.sessionState = sessionState
    if (typeof personaId === 'string' && personaId.trim()) payload.personaId = personaId.trim()
    if (normalizedAgentVersion) payload.agentVersion = normalizedAgentVersion
    if (agentOptions && typeof agentOptions === 'object') payload.agentOptions = agentOptions
    const { data } = await callUser('aiRun', payload)
    if (data?.error) {
      const msg = data.error?.message || data.error?.code || 'AI run failed'
      throw new Error(msg)
    }
    if (!data?.jobId) throw new Error('AI run failed')

    const jobId = data.jobId
    return await new Promise((resolve, reject) => {
      let timer = null
      const unsub = watchJob(jobId, (job) => {
        if (!job) return
        if (job.stream && typeof onStream === 'function') {
          try {
            onStream(job.stream, job)
          } catch (e) {
            console.warn('[AiChatService] stream callback failed', e)
          }
        }
        if (job.status === 'completed') {
          try {
            if (timer) clearTimeout(timer)
          } catch {
            /* ignore */
          }
          unsub()
          resolve({
            jobId,
            sessionId: data?.sessionId || null,
            turnId: data?.turnId || null,
            status: 'completed',
            result: job.result,
            stream: job.stream
          })
        } else if (job.status === 'failed') {
          try {
            if (timer) clearTimeout(timer)
          } catch {
            /* ignore */
          }
          unsub()
          reject(new Error(job.error || 'AI run failed'))
        } else if (job.status === 'cancelled') {
          try {
            if (timer) clearTimeout(timer)
          } catch {
            /* ignore */
          }
          unsub()
          reject(new Error('stream_cancelled'))
        }
      })
      timer = setTimeout(
        async () => {
          try {
            unsub()
          } catch {
            /* ignore */
          }
          try {
            const snap = await getDoc(doc(db, 'aiJobs', jobId))
            const job = snap.exists() ? snap.data() : null
            if (job?.status === 'completed') {
              resolve({
                jobId,
                sessionId: data?.sessionId || null,
                turnId: data?.turnId || null,
                status: 'completed',
                result: job.result,
                stream: job.stream
              })
              return
            }
            if (job?.status === 'failed') {
              reject(new Error(job.error || 'AI run failed'))
              return
            }
            if (job?.status === 'cancelled') {
              reject(new Error('stream_cancelled'))
              return
            }
          } catch {
            /* ignore final fetch */
          }
          reject(new Error('AI run timed out'))
        },
        Math.max(5000, timeoutMs)
      )
    })
  }

  // Cancellable variant returning { promise, cancel }
  static runTurnStreamingCtl({
    messages,
    tools,
    tool_choice,
    model,
    provider = 'openrouter',
    files,
    onStream,
    agentMode = false,
    agentVersion,
    agentOptions,
    contextSnapshot,
    sessionId,
    turnId,
    latestUserMessage,
    clarificationResolution,
    sessionState,
    personaId,
    timeoutMs = 180000
  } = {}) {
    const normalizedAgentMode = !!agentMode
    const normalizedAgentVersion = normalizedAgentMode ? agentVersion || 'v2' : agentVersion
    const payload = {
      provider,
      model,
      waitForCompletion: false,
      purpose: 'chat',
      disableSystem: false,
      tool_choice: tool_choice || 'auto',
      stream: true,
      agentMode: normalizedAgentMode
    }
    if (Array.isArray(messages)) payload.messages = messages
    if (Array.isArray(tools) && !normalizedAgentMode) payload.tools = tools
    if (Array.isArray(files)) payload.files = files
    if (contextSnapshot && typeof contextSnapshot === 'object') payload.contextSnapshot = contextSnapshot
    if (typeof sessionId === 'string' && sessionId.trim()) payload.sessionId = sessionId.trim()
    if (typeof turnId === 'string' && turnId.trim()) payload.turnId = turnId.trim()
    if (typeof latestUserMessage === 'string' && latestUserMessage.trim()) {
      payload.latestUserMessage = latestUserMessage.trim()
    }
    if (clarificationResolution && typeof clarificationResolution === 'object') {
      payload.clarificationResolution = clarificationResolution
    }
    if (sessionState && typeof sessionState === 'object') payload.sessionState = sessionState
    if (typeof personaId === 'string' && personaId.trim()) payload.personaId = personaId.trim()
    if (normalizedAgentVersion) payload.agentVersion = normalizedAgentVersion
    if (agentOptions && typeof agentOptions === 'object') payload.agentOptions = agentOptions

    let unsub = null
    let timer = null
    let cancelled = false
    let settle = null
    let activeJobId = null
    const cancel = async () => {
      if (cancelled) return
      cancelled = true
      try {
        if (timer) clearTimeout(timer)
      } catch {
        /* ignore timer cleanup errors */
      }
      try {
        if (typeof unsub === 'function') unsub()
      } catch {
        /* ignore unsubscribe cleanup errors */
      }
      if (activeJobId) {
        try {
          await callUser('aiChatCancelJob', { jobId: activeJobId })
        } catch {
          /* ignore backend cancellation errors */
        }
      }
      if (typeof settle === 'function') {
        settle({ ok: false, error: new Error('stream_cancelled') })
      }
    }

    const promise = (async () => {
      const { data } = await callUser('aiRun', payload)
      if (data?.error) throw new Error(data.error?.message || data.error?.code || 'AI run failed')
      if (!data?.jobId) throw new Error('AI run failed')
      const jobId = data.jobId
      activeJobId = jobId
      return await new Promise((resolve, reject) => {
        settle = ({ ok, value, error }) => {
          if (!settle) return
          const finalize = settle
          settle = null
          finalize._done = true
          if (ok) resolve(value)
          else reject(error)
        }
        if (cancelled) return settle({ ok: false, error: new Error('stream_cancelled') })
        unsub = watchJob(jobId, (job) => {
          if (!job || cancelled) return
          if (job.stream && typeof onStream === 'function') {
            try {
              onStream(job.stream, job)
            } catch (e) {
              console.warn('[AiChatService] stream callback failed', e)
            }
          }
          if (job.status === 'completed') {
            try {
              if (timer) clearTimeout(timer)
            } catch {
              /* ignore timer cleanup errors */
            }
            try {
              if (typeof unsub === 'function') unsub()
            } catch {
              /* ignore unsubscribe cleanup errors */
            }
            settle({
              ok: true,
              value: {
                jobId,
                sessionId: data?.sessionId || null,
                turnId: data?.turnId || null,
                status: 'completed',
                result: job.result,
                stream: job.stream
              }
            })
          } else if (job.status === 'failed') {
            try {
              if (timer) clearTimeout(timer)
            } catch {
              /* ignore timer cleanup errors */
            }
            try {
              if (typeof unsub === 'function') unsub()
            } catch {
              /* ignore unsubscribe cleanup errors */
            }
            settle({ ok: false, error: new Error(job.error || 'AI run failed') })
          } else if (job.status === 'cancelled') {
            try {
              if (timer) clearTimeout(timer)
            } catch {
              /* ignore timer cleanup errors */
            }
            try {
              if (typeof unsub === 'function') unsub()
            } catch {
              /* ignore unsubscribe cleanup errors */
            }
            settle({ ok: false, error: new Error('stream_cancelled') })
          }
        })
        timer = setTimeout(
          async () => {
            try {
              if (typeof unsub === 'function') unsub()
            } catch {
              /* ignore unsubscribe cleanup errors */
            }
            try {
              const snap = await getDoc(doc(db, 'aiJobs', jobId))
              const job = snap.exists() ? snap.data() : null
              if (job?.status === 'completed') {
                settle({
                  ok: true,
                  value: {
                    jobId,
                    sessionId: data?.sessionId || null,
                    turnId: data?.turnId || null,
                    status: 'completed',
                    result: job.result,
                    stream: job.stream
                  }
                })
                return
              }
              if (job?.status === 'failed') {
                settle({ ok: false, error: new Error(job.error || 'AI run failed') })
                return
              }
              if (job?.status === 'cancelled') {
                settle({ ok: false, error: new Error('stream_cancelled') })
                return
              }
            } catch {
              /* ignore final fetch errors */
            }
            settle({ ok: false, error: new Error('AI run timed out') })
          },
          Math.max(5000, timeoutMs)
        )
      })
    })()

    return { promise, cancel }
  }
}
